import { formatarIdentificacaoObra } from "@/shared/utils/formatar-identificacao-obra";
import { OBRA_SITUACAO_CANCELADA } from "./carga-de-trabalho.types";
import type { AlocacaoDeObra, ConflitoIgnorado, ObraComAlocacoes } from "./carga-de-trabalho.types";

/** Trecho em que duas alocações de obras diferentes ocupam o mesmo Local. */
export interface ConflitoLocal {
  a: AlocacaoDeObra;
  b: AlocacaoDeObra;
  /** `yyyy-MM-dd` */
  inicio: string;
  fim: string;
  /** Marcado como tratado (HU 2042): aparece em verde e não oferece "Resolver Conflito". */
  tratado: boolean;
}

/**
 * Conflito (HU 2023): duas alocações de obras diferentes do mesmo Local que se
 * sobrepõem no tempo (intervalos fechados; datas `yyyy-MM-dd` comparam
 * corretamente como texto). Calculado no front; os detalhes seguem pendentes na HU.
 */
export function detectConflitos(alocacoes: AlocacaoDeObra[]): ConflitoLocal[] {
  const conflitos: ConflitoLocal[] = [];

  for (let i = 0; i < alocacoes.length; i += 1) {
    for (let j = i + 1; j < alocacoes.length; j += 1) {
      const a = alocacoes[i];
      const b = alocacoes[j];
      if (!a || !b || a.obra.id === b.obra.id) continue;

      const inicio = a.inicio > b.inicio ? a.inicio : b.inicio;
      const fim = a.fim < b.fim ? a.fim : b.fim;
      if (inicio <= fim) conflitos.push({ a, b, inicio, fim, tratado: false });
    }
  }

  return conflitos;
}

/** O conflito é tratado quando período e par de alocações batem com um registro ignorado. */
export function isConflitoTratado(
  conflito: Pick<ConflitoLocal, "a" | "b" | "inicio" | "fim">,
  ignorados: ConflitoIgnorado[],
): boolean {
  return ignorados.some(
    (ignorado) =>
      ignorado.conflitoInicio.slice(0, 10) === conflito.inicio &&
      ignorado.conflitoFim.slice(0, 10) === conflito.fim &&
      ignorado.alocacaoIds.includes(conflito.a.id) &&
      ignorado.alocacaoIds.includes(conflito.b.id),
  );
}

/** Conflitos ainda não tratados: são os que contam no selo e permitem resolução. */
export function conflitosAbertos(conflitos: ConflitoLocal[]): ConflitoLocal[] {
  return conflitos.filter((conflito) => !conflito.tratado);
}

/**
 * Obra cancelada não ocupa o Local: fica fora do cálculo de conflito. Os `ignorados`
 * (vindos de `conflitosIgnorados`) marcam os conflitos já tratados.
 */
export function conflitosDoLocal(
  alocacoes: AlocacaoDeObra[],
  ignorados: ConflitoIgnorado[] = [],
): ConflitoLocal[] {
  return detectConflitos(
    alocacoes.filter((item) => item.obra.situacao !== OBRA_SITUACAO_CANCELADA),
  ).map((conflito) => ({ ...conflito, tratado: isConflitoTratado(conflito, ignorados) }));
}

/** Conflitos por alocação na perspectiva por Obra: agrega as alocações de todas as obras por Local. */
export function conflitosPorAlocacaoDeLocal(
  obras: ObraComAlocacoes[],
  ignorados: ConflitoIgnorado[] = [],
): Map<string, ConflitoLocal[]> {
  const byLocal = new Map<string, AlocacaoDeObra[]>();

  for (const obra of obras) {
    for (const alocacao of obra.alocacoes) {
      const bucket = byLocal.get(alocacao.local.id) ?? [];
      bucket.push({
        id: alocacao.id,
        inicio: alocacao.inicio,
        fim: alocacao.fim,
        obra: {
          id: obra.id,
          codigo: obra.codigo,
          nome: obra.nome,
          situacao: obra.situacao,
          embarcacao: obra.embarcacao,
        },
        status: alocacao.status,
      });
      byLocal.set(alocacao.local.id, bucket);
    }
  }

  const result = new Map<string, ConflitoLocal[]>();
  for (const bucket of byLocal.values()) {
    for (const conflito of conflitosDoLocal(bucket, ignorados)) {
      for (const id of [conflito.a.id, conflito.b.id]) {
        result.set(id, [...(result.get(id) ?? []), conflito]);
      }
    }
  }
  return result;
}

export interface ConflitoLinha {
  obra: string;
  evento: string;
}

/** `yyyy-MM-dd` -> `dd/MM/yyyy`. */
export function formatDateBr(value: string): string {
  const [year = "", month = "", day = ""] = value.slice(0, 10).split("-");
  return `${day}/${month}/${year}`;
}

/** Linhas do tooltip: a obra que começa depois "inicia em"; a outra "termina em". */
export function describeConflito(conflito: ConflitoLocal): ConflitoLinha[] {
  const { a, b } = conflito;
  const [later, other] = a.inicio >= b.inicio ? [a, b] : [b, a];
  const label = (item: AlocacaoDeObra) => `Obra ${formatarIdentificacaoObra(item.obra)}`;

  return [
    { obra: label(later), evento: `inicia em ${formatDateBr(later.inicio)}` },
    { obra: label(other), evento: `termina em ${formatDateBr(other.fim)}` },
  ];
}

/** Duração inclusiva: `20d`, `2m`, `1m 15d` (mês comercial de 30 dias). */
export function formatDuracao(inicio: string, fim: string): string {
  const toUtc = (value: string) => {
    const [year = 1970, month = 1, day = 1] = value.slice(0, 10).split("-").map(Number);
    return Date.UTC(year, month - 1, day);
  };
  if (!/^\d{4}-\d{2}-\d{2}/.test(inicio) || !/^\d{4}-\d{2}-\d{2}/.test(fim)) return "";
  const days = Math.max(Math.round((toUtc(fim) - toUtc(inicio)) / 86_400_000) + 1, 1);
  const months = Math.floor(days / 30);
  const rest = days % 30;
  if (months === 0) return `${rest}d`;
  return rest === 0 ? `${months}m` : `${months}m ${rest}d`;
}
